import { ReactNode, useCallback, useRef } from 'react'

import { EditorContent } from '@tiptap/react'
import { FileDrop } from 'react-file-drop'

import { useEmbed } from '@tribeplatform/react-sdk/hooks'

import { AppsEnum } from '../common/constants/app.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { AudioPickerModal } from '../Picker/AudioPickerModal.js'
import { GiphyPickerModal } from '../Picker/GiphyPickerModal.js'
import { ImagePickerModal } from '../Picker/ImagePickerModal.js'
import { VideoPickerModal } from '../Picker/VideoPickerModal.js'
import { PollFormDialog } from '../Poll/PollFormDialog.js'

import { EmbedFormDialog } from './components/EmbedFormDialog.js'
import { EventFormDialog } from './components/EventFormDialog.js'
import { LinkFormDialog } from './components/LinkFormDialog.js'
import { LinkFormattingBubbleMenu } from './extensions/bubble-menu/link.js'
import { TextFormattingBubbleMenu } from './extensions/bubble-menu/text.js'
import { ImageMenu } from './extensions/image/menu/ImageMenu.js'
import { PollMenu } from './extensions/poll/PollMenu.js'
import {
  TableColumnMenu,
  TableRowMenu,
} from './extensions/table/components/menus/index.js'
import { VideoMenu } from './extensions/video/menu/VideoMenu.js'
import { useComposer } from './hooks/useComposer.js'
import { composerError, embedErrorMessage } from './utils/errors.js'

export interface ComposerProps {
  children?: ReactNode
  /**
   * Specifies the minimum size of the editor based on number of lines
   * Default: 1
   */
  minLines?: number
}

export function Composer({ children, minLines = 1 }: ComposerProps) {
  const intl = useI18n()
  const { $t } = intl
  const { asyncQuery: embed } = useEmbed()
  const { state, toggleDialog, editor, onFileUpload, poll } = useComposer()

  const onEditorClick = useCallback(() => {
    if (editor && editor.isEmpty && !editor.isFocused) {
      editor.commands.focus()
    }
  }, [editor])

  const onEmbed = useCallback(
    async (data: { link: string }) => {
      const { link } = data
      if (link) {
        const oembed = await embed({ url: link })
        if (oembed.html) {
          const { from } = editor.state.selection
          editor
            .chain()
            .focus()
            .setEmbed({ embed: oembed })
            .setTextSelection(from + 1)
            .insertContent(' ')
            .run()
        }
      }
    },
    [editor, embed],
  )

  const menuContainerRef = useRef(null)

  if (!editor) {
    return null
  }

  return (
    <>
      <div
        ref={menuContainerRef}
        id="composer"
        className="h-full relative flex-col flex flex-1 min-h-[60px]"
      >
        <TextFormattingBubbleMenu editor={editor} />
        <LinkFormattingBubbleMenu editor={editor} />
        <TableRowMenu editor={editor} appendTo={menuContainerRef} />
        <TableColumnMenu editor={editor} appendTo={menuContainerRef} />
        <ImageMenu editor={editor} />
        <VideoMenu editor={editor} />
        <FileDrop
          className="flex flex-col flex-grow"
          targetClassName="flex flex-col flex-grow"
          onDrop={files => onFileUpload(files)}
        >
          <article className="flex flex-col flex-grow editor-content">
            <EditorContent
              editor={editor}
              onClick={onEditorClick}
              style={{ minHeight: `${minLines}em` }}
              className="text-md text-content relative flex-1 mb-4 flex flex-col"
            />
          </article>
        </FileDrop>
        <PollMenu editor={editor} />
        {children}
      </div>
      {/* These dialogs should have lived within the provider but due to a issue with the modal portals we need to keep them close to the editor  */}
      <ImagePickerModal
        open={state.image}
        multiple
        close={() => toggleDialog('image', false)}
        upload={onFileUpload}
      />
      <GiphyPickerModal
        open={state.giphy}
        close={() => toggleDialog('giphy', false)}
        onUpload={file => {
          onFileUpload([file], { attrs: { app: AppsEnum.Giphy } })
          toggleDialog('giphy', false)
        }}
      />
      <VideoPickerModal
        open={state.video}
        close={() => toggleDialog('video', false)}
        onUpload={files => {
          onFileUpload(files)
          toggleDialog('video', false)
        }}
        onEmbed={async link => {
          try {
            await onEmbed({ link })
            toggleDialog('video', false)
          } catch (e) {
            composerError(intl)(
              $t({
                id: 'Composer.Composer.InvalidUrl',
                defaultMessage: 'The url that you entered is not valid',
              }),
            )
          }
        }}
      />
      <AudioPickerModal
        open={state.audio}
        close={() => toggleDialog('audio', false)}
        onUpload={files => {
          onFileUpload(files)
          toggleDialog('audio', false)
        }}
        onEmbed={async link => {
          try {
            await onEmbed({ link })
            toggleDialog('audio', false)
          } catch (e) {
            composerError(intl)(
              $t({
                id: 'Composer.Composer.InvalidUrl',
                defaultMessage: 'The url that you entered is not valid',
              }),
            )
          }
        }}
      />
      <LinkFormDialog
        open={state.link}
        onClose={() => toggleDialog('link', false)}
        onSubmit={data => {
          const { link, text } = data
          if (link) {
            editor
              .chain()
              .focus()
              .insertLink({ href: link, text })
              .insertContent(' ')
              .run()
          }
          toggleDialog('link', false)
        }}
      />
      <EmbedFormDialog
        embedItem={state.embedItem}
        open={state.embed}
        onClose={() => toggleDialog('embed', false)}
        onSubmit={async (data, methods) => {
          try {
            await onEmbed(data)
            toggleDialog('embed', false)
          } catch (e) {
            methods?.setError('link', {
              type: 'validate',
              message: embedErrorMessage(intl)(e),
            })
          }
        }}
      />
      {poll && (
        <PollFormDialog
          id={
            editor.options.poll?.state === 'updating'
              ? editor.options.poll.id
              : undefined
          }
          open={!!editor.options.poll}
          onCancel={() => {
            editor.commands.closePoll()
          }}
          poll={poll}
          onSuccess={poll => {
            editor.commands.insertPoll({ id: poll.id })
          }}
        />
      )}
      {state.event && (
        <EventFormDialog
          open={state.event}
          onClose={() => toggleDialog('event', false)}
          onSubmit={async id => {
            editor.commands.insertEvent({ id })
            toggleDialog('event', false)
          }}
        />
      )}
    </>
  )
}
